import { RovoIcon } from '@atlaskit/logo';
import { AppLogo, TopNav, TopNavEnd, TopNavStart } from '@atlaskit/navigation-system';
import Lozenge from '@atlaskit/lozenge';
import React, { useCallback, useMemo } from 'react';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { FormattedMessage } from 'react-intl';
import { useAtomValue } from 'jotai';
import { TwgAppSwitcher } from '../AppSwitcher';
import { ExplorerIcon } from '../ExplorerIcon';
import AccountMenu from '@atlassian/account-menu';
import { useAtlassianUser } from '../../../hooks/use-atlassian-user';
import { getConfig } from '../../../config';
import { useColorMode, useSetColorMode } from '@atlaskit/app-provider';
import { messages } from './messages';
import { NavActionsAtom } from '../../state/NavActionsAtom';
import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';

const styles = cssMap({
	labelContainer: {
		display: 'flex',
		alignItems: 'center',
	},
});

export const NavigationBar = (): React.JSX.Element => {
	const user = useAtlassianUser();
	const navActions = useAtomValue(NavActionsAtom);
	const { fireEvent } = useAnalyticsEvents();

	const colorMode = useColorMode();
	const setColorMode = useSetColorMode();

	const handleThemeChange = React.useCallback(
		(theme: 'light' | 'dark' | 'auto') => {
			setColorMode(theme);
		},
		[setColorMode],
	);

	const handleIconClick = useCallback(() => {
		fireEvent('ui.button.clicked.gcNavIconClick', {});
		window.location.href = '/graph-explorer';
	}, [fireEvent]);

	const appLabel = useMemo(() => {
		const path = typeof window !== 'undefined' ? window.location.pathname : '';
		if (path.startsWith('/labs')) {
			return 'Labs';
		}
		if (path.startsWith('/catalog')) {
			return 'Catalog';
		}
		if (path.startsWith('/assistant')) {
			return 'Assistant';
		}
		if (path.startsWith('/visualiser')) {
			return 'Visualiser';
		}
		if (path.startsWith('/query-builder')) {
			return 'Query Builder';
		}
		if (path.startsWith('/graph-explorer')) {
			return 'Graph Explorer';
		}
		if (path.startsWith('/cypher-explorer')) {
			return 'Cypher Explorer';
		}
		if (path.startsWith('/workstreams') || path.startsWith('/inferred-projects')) {
			return 'Workstreams';
		}
		return 'Teamwork Graph';
	}, []);

	return (
		<TopNav>
			<TopNavStart sideNavToggleButton={null}>
				<Flex direction="row">
					<TwgAppSwitcher />
					<Flex direction="row" gap="space.025">
						<AppLogo
							href={appLabel === 'Graph Explorer' ? '/graph-explorer' : '/'}
							icon={appLabel === 'Graph Explorer' ? ExplorerIcon : RovoIcon}
							name={appLabel}
							label={appLabel === 'Graph Explorer' ? 'Graph Explorer home' : ''}
							onClick={appLabel === 'Graph Explorer' ? handleIconClick : undefined}
						/>
						{appLabel && (
							<Box xcss={styles.labelContainer}>
								{(appLabel === 'Labs' ||
									appLabel === 'Query Builder' ||
									appLabel === 'Assistant') && (
									<Lozenge appearance={appLabel === 'Assistant' ? 'neutral' : 'new'} isBold>
										<FormattedMessage
											{...(appLabel === 'Assistant'
												? messages.underDevelopmentTag
												: messages.betaTag)}
										/>
									</Lozenge>
								)}
							</Box>
						)}
					</Flex>
				</Flex>
			</TopNavStart>
			<TopNavEnd>
				{navActions}
				<AccountMenu
					name={user.name}
					picture={user.picture}
					email={user.email}
					viewProfileLink={`${getConfig().atlassianHomeUrl}/people/${encodeURIComponent(user.accountId)}`}
					manageProfileLink={`${getConfig().atlassianAccountUrl}/login?application=dtac&continue=${encodeURIComponent(
						`${getConfig().atlassianAccountUrl}/manage-profile`,
					)}&login_hint=${encodeURIComponent(user.email)}&prompt=none`}
					logOutLink={`${getConfig().atlassianAccountUrl}/logout?application=dtac&continue=${encodeURIComponent(
						getConfig().serviceUrl,
					)}`}
					onThemeChange={handleThemeChange}
					currentTheme={colorMode}
				/>
			</TopNavEnd>
		</TopNav>
	);
};
